import { InfoState } from '../../../components/ui';
import { friendlyMessage } from '../../../components/ui/friendlyMessage';
import React from "react";
import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import TraineeGraph from "./TraineeGraph";
import CourseGraph from "./CourseGraph";
import RecentPushedAnnouncement from "./RecentPushedAnnouncement";
import Ticket from "./Ticket";
import Loader from "../../../components/admin/Loader";
import { getDashboardData } from "../../../services/organization/dashboard/dashboard.service";
import { usePermissions } from "../../../context/PermissionsContext";
const toneBackgrounds = {
  "primary": "[--tw-bg-opacity:1] tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] ui-bg-primary-subtle",
  "secondary": "[--tw-bg-opacity:1] tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-bg-secondary-subtle",
  "success": "[--tw-bg-opacity:1] tw-bg-[rgb(236_253_245_/_var(--tw-bg-opacity,_1))] ui-bg-success-subtle",
  "danger": "[--tw-bg-opacity:1] tw-bg-[rgb(255_241_242_/_var(--tw-bg-opacity,_1))] ui-bg-danger-subtle",
  "warning": "[--tw-bg-opacity:1] tw-bg-[rgb(255_251_235_/_var(--tw-bg-opacity,_1))] ui-bg-warning-subtle",
  "info": "[--tw-bg-opacity:1] tw-bg-[rgb(240_249_255_/_var(--tw-bg-opacity,_1))] ui-bg-info-subtle",
  "light": "[--tw-bg-opacity:1] tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-bg-light-subtle",
  "dark": "[--tw-bg-opacity:1] tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-bg-dark-subtle"
};

const toneForegrounds = {
  "primary": "[--tw-text-opacity:1] tw-text-[rgb(64_84_214_/_var(--tw-text-opacity,_1))] ui-text-primary",
  "secondary": "[--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] ui-text-secondary",
  "success": "[--tw-text-opacity:1] tw-text-[rgb(5_150_105_/_var(--tw-text-opacity,_1))] ui-text-success",
  "danger": "[--tw-text-opacity:1] tw-text-[rgb(225_29_72_/_var(--tw-text-opacity,_1))] ui-text-danger",
  "warning": "[--tw-text-opacity:1] tw-text-[rgb(217_119_6_/_var(--tw-text-opacity,_1))] ui-text-warning",
  "info": "[--tw-text-opacity:1] tw-text-[rgb(2_132_199_/_var(--tw-text-opacity,_1))] ui-text-info",
  "light": "[--tw-text-opacity:1] tw-text-[rgb(248_250_252_/_var(--tw-text-opacity,_1))] ui-text-light",
  "dark": "[--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] ui-text-dark"
};


function Dashboard() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [allDashboardData, setAllDashboardData] = useState(null);
  const [error, setError] = useState(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDashboardData();

      if (response.success) {
        setAllDashboardData(response.data);
      } else {
        setError("Failed to fetch dashboard data");
      }
    } catch (error) {

      setError("An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Generate dashboard cards data from API response
  const getDashboardCards = () => {
    if (!allDashboardData) return [];

    return [
      {
        id: 1,
        title: "Total Users",
        value: allDashboardData.totalUsers || 0,
        icon: "lucide:users",
        color: "info",
        redirectTo: "/admin/trainees"
      },
      {
        id: 2,
        title: "Active Users",
        value: allDashboardData.activeUsers || 0,
        icon: "lucide:user-check",
        color: "success",
        redirectTo: "/admin/trainees?status=active"
      },
      {
        id: 3,
        title: "Blocked Users",
        value: allDashboardData.inactiveUsers || 0,
        icon: "lucide:user-x",
        color: "danger",
        redirectTo: "/admin/trainees?status=inactive"
      },
      {
        id: 4,
        title: "Total Courses",
        value: allDashboardData.totalCourses || 0,
        icon: "lucide:book-copy",
        color: "info",
        redirectTo: "/admin/courses"
      },
      {
        id: 5,
        title: "Active Courses",
        value: allDashboardData.activeCourses || 0,
        icon: "lucide:book-open",
        color: "success",
        redirectTo: "/admin/courses?filter=active"
      },
      {
        id: 6,
        title: "Approved Courses",
        value: allDashboardData.approvedCourses || 0,
        icon: "lucide:badge-check",
        color: "success",
        redirectTo: "/admin/courses?tab=approved"
      },
      {
        id: 7,
        title: "Free Courses",
        value: allDashboardData.freeCourses || 0,
        icon: "lucide:gift",
        color: "warning",
        redirectTo: "/admin/courses?filter=free"
      },
      {
        id: 8,
        title: "Paid Courses",
        value: allDashboardData.paidCourses || 0,
        icon: "lucide:credit-card",
        color: "info",
        redirectTo: "/admin/courses?filter=paid"
      },
      {
        id: 9,
        title: "Total Classrooms",
        value: allDashboardData.totalClassrooms || 0,
        icon: "lucide:building",
        color: "info",
        redirectTo: "/admin/classrooms"
      },
      {
        id: 10,
        title: "Active Classrooms",
        value: allDashboardData.activeClassrooms || 0,
        icon: "lucide:layout-grid",
        color: "success",
        redirectTo: "/admin/classrooms?status=active"
      },
      {
        id: 11,
        title: "Inactive Classrooms",
        value: allDashboardData.inactiveClassrooms || 0,
        icon: "lucide:lock",
        color: "danger",
        redirectTo: "/admin/classrooms?status=inactive"
      },
      {
        id: 12,
        title: "Total Trainees",
        value: allDashboardData.totalTrainees || 0,
        icon: "lucide:users",
        color: "info",
        redirectTo: "/admin/trainees"
      },
      {
        id: 13,
        title: "Active Trainees",
        value: allDashboardData.activeTrainees || 0,
        icon: "lucide:user-check",
        color: "success",
        redirectTo: "/admin/trainees?status=active"
      },
      {
        id: 14,
        title: "Inactive Trainees",
        value: allDashboardData.inactiveTrainees || 0,
        icon: "lucide:user-x",
        color: "warning",
        redirectTo: "/admin/trainees?status=inactive"
      },
      {
        id: 15,
        title: "Pending Tickets",
        value: allDashboardData.pendingTickets || 0,
        icon: "lucide:ticket",
        color: "danger",
        redirectTo: "/admin/settings?activeTab=query&status=Pending"
      },
      {
        id: 16,
        title: "Resolved Tickets",
        value: allDashboardData.resolvedTickets || 0,
        icon: "lucide:ticket-check",
        color: "success",
        redirectTo: "/admin/settings?activeTab=query&status=Solved"
      }
    ];
  };

  const handleCardClick = (card) => {
    if (card.redirectTo) {
      navigate(card.redirectTo);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '400px' }}>
        <Loader />
      </div>
    );
  }

  // Show error state
  if (error) return <InfoState title="Your dashboard is taking a little longer" message={friendlyMessage(error)} onRetry={fetchDashboardData} />;

  const dashboardCards = getDashboardCards();

  return (
    <>
      {/* Dashboard Cards */}
      {permissions.dashboard_management_cards_view && (
        <div className={"tw-flex tw-flex-wrap tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] [--grid-x:1.5rem] [--grid-y:1.5rem] ui-row tw-gap-grid-4"}>
          {dashboardCards.map((item) => (
            <div key={item.id} className="tw-min-w-[0px] ui-column tw-w-full sm:tw-w-1/2 md:tw-w-1/3 lg:tw-w-1/4">
              <div
                className={`[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-h-full   ${item.redirectTo ? "!tw-cursor-pointer cursor-pointer" : ""}`}
                onClick={() => item.redirectTo && handleCardClick(item)}
                style={item.redirectTo ? { transition: 'transform 0.2s', cursor: 'pointer' } : {}}
                onMouseOver={(e) => item.redirectTo && (e.currentTarget.style.transform = 'translateY(-5px)')}
                onMouseOut={(e) => item.redirectTo && (e.currentTarget.style.transform = 'translateY(0)')}
              >
                <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body tw-flex tw-items-center">
                  <div
                    className={`${toneBackgrounds[item.color] || ""} tw-flex tw-h-14 tw-w-14 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-me-4`}
                  >
                    <Icon
                      icon={item.icon}
                      width="28"
                      height="28"
                      className={`${toneForegrounds[item.color] || ""}`}
                      aria-hidden="true"
                    />
                  </div>
                  <div>
                    <h6 className="!tw-text-[var(--secondary-color)] tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-text-opacity:1] ui-card-subtitle tw-mb-1 tw-text-slate-500">
                      {item.title}
                    </h6>
                    <h2 className="tw-text-[1rem] tw-font-semibold tw-text-[#2c3e50] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] tw-overflow-hidden tw-leading-[1.5rem] ui-card-title tw-mb-0">{item.value}</h2>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Charts Section */}
      {permissions.dashboard_management_graphs_view && (
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mt-6"}>
          <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2">
            <TraineeGraph
              data={allDashboardData?.monthlyTraineeJoins || []}
              loading={loading}
            />
          </div>
          <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2">
            <CourseGraph
              data={allDashboardData?.monthlyCourseAdds || []}
              loading={loading}
            />
          </div>
        </div>
      )}

      {/* Tickets and Announcements Section */}
      {permissions.dashboard_management_view_latest_updates_view && (
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mt-6"}>
          <div className="tw-min-w-0 tw-w-full ui-column md:tw-w-1/2">
            <Ticket
              tickets={allDashboardData?.latestSupportTickets || []}
              loading={loading}
              canViewModule={Boolean(permissions.settings_query_view)}
            />
          </div>
          <div className="tw-min-w-0 tw-w-full ui-column md:tw-w-1/2">
            <RecentPushedAnnouncement
              announcements={allDashboardData?.latestAnnouncements || []}
              loading={loading}
              canViewModule={Boolean(permissions.announcement_management_view)}
            />
          </div>
        </div>
      )}

      {/* Show message if no permissions */}
      {!permissions.dashboard_management_cards_view &&
       !permissions.dashboard_management_graphs_view &&
       !permissions.dashboard_management_view_latest_updates_view && (
        <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '400px' }}>
          <div className="tw-text-center">
            <Icon icon="lucide:shield-x" width="48" height="48" className="!tw-text-[var(--warning-color)] tw-text-amber-600 tw-mb-4" />
            <h5 className="!tw-text-[var(--warning-color)] tw-text-amber-600">Access Restricted</h5>
            <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500">You don't have permission to view any dashboard content.</p>
          </div>
        </div>
      )}
    </>
  );
}

export default Dashboard;
